<template>
  <section class="inputAuthCodeSection" :class="{ isPc: !isSp }">
    <TitleText text="認証コード入力" />
    <p class="mailAddress">{{ mailAddress.emailAccount }}@{{ mailAddress.emailDomain }}</p>
    <TextGeneral class="text">
      上記メールアドレス宛にメールを送信しました。<br />メールに記載の認証コードを入力してください。
    </TextGeneral>
    <!-- 認証コードはセキュリティ観点上詳細なバリデーションチェックを行わない -->
    <InputAreaWithValidation
      v-model="inputAuthCode"
      class="inputAuthCode"
      area-label="認証コード"
      input-type="text"
      placeholder="例）ng6xp3"
      :error-from-outer="authCodeValidationError"
      @watchValidation="watchAuthCodeValidation"
    />
    <ServerErrorArea class="serverErrorArea" :error-type="errorType" :server-error-list="serverErrorList" />
    <ButtonTypeCustom
      button-type="button"
      button-text="認証する"
      button-color="colorPrimary"
      button-size="sizeLarge"
      :class="{ isDisabled: !canSendRequest }"
      class="authButton"
      @button-clicked="sendAuth"
    />
    <p class="note">
      ※ドメイン指定、迷惑メール設定によりメールが届かない場合があります。＠shingakunet.comからのメールが受信できるよう設定を変更してください。
    </p>
    <div class="rightItems">
      <p class="resendMail" :class="{ isPc: !isSp }">
        <TextLink arrow-type="rightArrowRight" link-text="メールを再送信する" @onClick="onClickResendMail" />
      </p>
      <p>
        <TextLink arrow-type="rightArrowRight" link-text="メールアドレスを修正する" @onClick="fixMailAddress" />
      </p>
    </div>
    <!-- 送信確認 -->
    <Confirm
      :is-open="isResendConfirmOpen"
      :text="'メールを再送信します。\nよろしいですか？'"
      positive-custom-text="送信する"
      font-bold
      persistent
      @close-dialog="closeResendDialog"
    />
    <!-- 送信完了（正常終了） -->
    <Alert
      :is-open="isResendDoneDialogOpen"
      :text="'メールを再送信しました。'"
      persistent
      @close-dialog="closeResendAfterDialogs"
    />
    <!-- 送信完了（異常終了） -->
    <Error
      :is-open="isResendErrorDialogOpen"
      :title="'メールの再送信に失敗しました。'"
      :detail-error-list="resendErrorList"
      persistent
      :button-custom-text="'閉じる'"
      @click-dialog-button="closeResendAfterDialogs"
    />
  </section>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import TitleText from './TitleText.vue';
import TextGeneral from './TextGeneral.vue';
import ServerErrorArea from '../../../common/common/ServerErrorArea.vue';
import InputAreaWithValidation, { VALIDATION_LIST } from '../../../common/common/Input/InputWithLabel.vue';
import ButtonTypeCustom from '../../../common/common/Button/ButtonTypeCustom.vue';
import TextLink from '../../../common/common/Button/TextLink.vue';
import Confirm from '../../../common/common/Dialog/Confirm.vue';
import Alert from '../../../common/common/Dialog/Alert.vue';
import Error from '../../../common/common/Dialog/Error.vue';
import {
  sendToErrorPage,
  judgeResponseErrorType,
  ERROR_TYPES,
  sendToMaintenancePage,
} from '../../../../helper/_responseError';
import { registerErrorResultMapper } from '../../../common/common/_registerResponseCheck';
import { sendLog } from '../../../../helper/_sendLog';

const ERROR_MESSAGES = {
  START_OVER: 'お手数ですが、最初からやり直してください。',
  RETRY: 'お手数ですが、しばらく経ってからもう一度お試しください。',
};

export default {
  components: {
    TitleText,
    TextGeneral,
    InputAreaWithValidation,
    ButtonTypeCustom,
    TextLink,
    Confirm,
    Alert,
    Error,
    ServerErrorArea,
  },
  props: {
    mailAddress: {
      type: Object,
      required: true,
    },
    isSpFlg: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    errorGetMailAddress: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      inputAuthCode: '',
      authCodeValidationError: undefined,
      isAuthCodeHasError: undefined,
      isResendConfirmOpen: false,
      isResendDoneDialogOpen: false,
      isResendErrorDialogOpen: false,
      postProcessingFlg: false,
      serverErrorList: [],
      resendErrorList: [],
      errorTypeSendAuth: undefined,
    };
  },
  computed: {
    isSp() {
      if (typeof this.isSpFlg === 'boolean') return this.isSpFlg;
      return this.$isSp;
    },
    urlPrefix() {
      return this.$isSp ? 'smp2' : 'net2';
    },
    VALIDATION_LIST() {
      return VALIDATION_LIST;
    },
    canSendRequest() {
      if (this.inputAuthCode === '' || this.errorGetMailAddress) return false;
      return true;
    },
    hasServerError() {
      return this.serverErrorList.length !== 0 || this.expireFlg;
    },
    errorTypeGetMailAddress() {
      // アクション完了画面から認証コード入力画面へ遷移した且つメールアドレス取得エラーの場合、ERROR_TYPESの値をセットする
      if (this.errorGetMailAddress) return ERROR_TYPES.SYSTEM_ERROR;
      return undefined;
    },
    errorType() {
      return this.errorTypeSendAuth || this.errorTypeGetMailAddress;
    },
  },
  methods: {
    watchAuthCodeValidation(value) {
      // 共通のバリデーションチェックを使ってしまうと、リセット時に『フォーカスしてないときの再バリデーションチェック』でエラーを検出してしまうので、外でハンドリングする
      if (this.inputAuthCode === '') {
        this.authCodeValidationError = '認証コードが入力されていません。';
      } else {
        this.authCodeValidationError = '';
      }
      this.isAuthCodeHasError = value;
    },
    resetError() {
      this.errorTypeSendAuth = undefined;
      this.serverErrorList = [];
    },
    async sendAuth() {
      if (this.authCodeValidationError !== '') return;
      if (this.isAuthCodeHasError) return;
      if (this.postProcessingFlg) return;

      this.postProcessingFlg = true;

      const param = {
        authCode: this.inputAuthCode,
        'org.apache.struts.taglib.html.TOKEN': frontend.csrfToken,
      };
      axios
        .post(`/${this.urlPrefix}/quipMember/register/authJson/`, qs.stringify(param))
        .then((response) => {
          this.resetError();
          if (!response) throw error;
          if (!response.data) throw error;
          if (response.data.resultCode === 0) {
            this.$emit('goto-next');
          } else if (response.data.resultCode === 1) {
            const mappedResponse = registerErrorResultMapper(response);
            if (typeof mappedResponse === 'object') {
              this.serverErrorList = mappedResponse;
            } else {
              this.errorTypeSendAuth = mappedResponse;
            }
          } else if (response.data.resultCode === 2) {
            sendToMaintenancePage(this.isSp);
          } else {
            // 異常なresultCode
            throw error;
          }
        })
        .catch((error) => {
          this.resetError();
          const judgedErrorType = judgeResponseErrorType(error);
          if (judgedErrorType === ERROR_TYPES.OTHER || judgedErrorType === ERROR_TYPES.SYSTEM_ERROR) {
            sendToErrorPage(this.isSp);
          } else {
            this.errorTypeSendAuth = judgedErrorType;
          }
        })
        .finally(() => {
          this.postProcessingFlg = false;
        });
    },
    onClickResendMail(event) {
      sendLog(s, event.target, `${trace_p}_resend`);
      this.isResendConfirmOpen = true;
    },
    async closeResendDialog(value) {
      this.isResendConfirmOpen = false;
      if (value) {
        sendLog(s, document, `${trace_p}_resend_ok`);
        if (this.postProcessingFlg) return;

        this.postProcessingFlg = true;

        const param = {
          ...this.mailAddress,
          resendFlg: '1',
          'org.apache.struts.taglib.html.TOKEN': frontend.csrfToken,
        };
        axios
          .post(`/${this.urlPrefix}/quipMember/register/sendJson/`, qs.stringify(param))
          .then((response) => {
            if (!response) throw error;
            if (!response.data) throw error;
            if (response.data.resultCode === 0) {
              this.isResendDoneDialogOpen = true;
            } else if (response.data.resultCode === 1) {
              const mappedResponse = registerErrorResultMapper(response);
              if (typeof mappedResponse === 'object') {
                this.resendErrorList = mappedResponse;
                this.isResendErrorDialogOpen = true;
              } else if (mappedResponse === 2) {
                // トークンチェックエラー
                this.resendErrorList = [ERROR_MESSAGES.START_OVER];
                this.isResendErrorDialogOpen = true;
              } else {
                // 異常なレスポンス
                throw error;
              }
            } else if (response.data.resultCode === 2) {
              sendToMaintenancePage(this.isSp);
            } else {
              // 異常なresultCode
              throw error;
            }
          })
          .catch((error) => {
            const judgedErrorType = judgeResponseErrorType(error);
            if (judgedErrorType === ERROR_TYPES.SYSTEM_ERROR) {
              // システムエラー
              this.resendErrorList = [ERROR_MESSAGES.RETRY];
              this.isResendErrorDialogOpen = true;
            } else {
              // セッションエラー・その他エラー
              this.resendErrorList = [ERROR_MESSAGES.START_OVER];
              this.isResendErrorDialogOpen = true;
            }
          })
          .finally(() => {
            this.postProcessingFlg = false;
          });
      }
    },
    closeResendAfterDialogs() {
      this.isResendDoneDialogOpen = false;
      this.isResendErrorDialogOpen = false;
    },
    fixMailAddress(event) {
      sendLog(s, event.target, `${trace_p}_rewrite`);
      this.inputAuthCode = '';
      this.authCodeValidationError = '';
      this.resetError();
      this.$emit('fix-email');
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'css/contents/common/global';

.inputAuthCodeSection {
  padding: 24px 20px 40px;

  &.isPc {
    padding: 0;
  }

  .mailAddress {
    text-align: center;
    font-size: $text-size-paragraph2;
    font-weight: $text-weight-bold;
    line-height: $text-line-height-default;
    margin-bottom: 16px;
    overflow-wrap: break-word;
  }

  .text {
    margin-bottom: 24px;
    font-size: $text-size-paragraph2;
    line-height: $text-line-height-default;
  }

  .authButton {
    margin-top: 24px;
    margin-bottom: 16px;

    &.isDisabled {
      background-color: $color-background-primary-third-dark;
      pointer-events: none;
    }
  }

  .serverErrorArea {
    margin-top: 20px;
  }

  .note {
    font-size: $text-size-caption1;
    color: $color-text-secondry;
    margin-bottom: 24px;
    line-height: $text-line-height-default;
  }

  .rightItems {
    text-align: right;
    font-size: $text-size-paragraph2;
    line-height: $text-line-height-default;

    .resendMail {
      line-height: $text-line-height-default;
      margin-bottom: 16px;

      &.isPc {
        margin-bottom: 8px;
      }
    }
  }
}
</style>
